import { useCallback, useEffect, useState } from "react";
import { formatE4, parseEuroToE4 } from "../../shared/money.js";
import { api } from "../api.js";
import { AnleitungKnopf } from "../AnleitungKnopf.js";
import { STAND_HINWEIS_TAGE } from "../../shared/datum.js";

interface Kachel {
  code: string;
  bezeichnung: string;
  einheit: string;
  nachkommastellen: number;
  adapter: string;
  wert_e4: number | null;
  quelle_stand: string | null;
  abgerufen_am: number | null;
  herkunft: string | null;
  veraltet: boolean;
  lizenz_hinweis: string | null;
  fehler: string | null;
  /** Jahreszahl des Kontrakts — steht hinter der Bezeichnung. */
  titel_zusatz: string | null;
  /** Zeitraum, für den der Wert gelten muss: „August 2026". */
  zeitraum: string | null;
  sortierung: number;
  /** Alter des Standes in Tagen — Grundlage für den Hinweis unten. */
  stand_tage: number | null;
  /** Gilt der Wert nur für den laufenden Monat? Dann braucht es keinen Hinweis. */
  monatsgrenze: boolean;
}

/**
 * Workers laufen mit TZ=UTC. Ohne explizite Zeitzone stünde auf der Kachel
 * eine Uhrzeit, die zwei Stunden danebenliegt — hier wird zwar im Browser
 * formatiert, aber die Absicht soll festgeschrieben sein.
 */
const BERLIN: Intl.DateTimeFormatOptions = { timeZone: "Europe/Berlin" };

const zeitpunkt = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("de-DE", {
        ...BERLIN,
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

function alter(ms: number | null): string {
  if (!ms) return "nie abgerufen";
  const min = Math.floor((Date.now() - ms) / 60_000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} min`;
  const std = Math.floor(min / 60);
  if (std < 24) return `vor ${std} h`;
  return `vor ${Math.floor(std / 24)} Tagen`;
}

export function Dashboard({ istAdmin }: { istAdmin: boolean }) {
  const [kacheln, setKacheln] = useState<Kachel[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [frischt, setFrischt] = useState(false);

  const auffrischen = useCallback(async () => {
    setFrischt(true);
    try {
      setKacheln(await api.post<Kachel[]>("/marktdaten/auffrischen", {}));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Auffrischen fehlgeschlagen.");
    } finally {
      setFrischt(false);
    }
  }, []);

  useEffect(() => {
    // Gespeicherten Stand zuerst — der Abruf hängt nicht am kritischen Pfad.
    api
      .get<Kachel[]>("/marktdaten")
      .then((k) => {
        setKacheln(k);
        if (k.some((x) => x.veraltet)) void auffrischen();
      })
      .catch((e) => setFehler(e.message));
  }, [auffrischen]);

  if (fehler && !kacheln)
    return (
      <div className="meldung fehler" role="alert">
        {fehler}
      </div>
    );
  if (!kacheln) return <p className="leer">Marktdaten werden geladen …</p>;

  return (
    <>
      <div className="pos-kopf" style={{ marginBottom: "1rem" }}>
        <h1 style={{ margin: 0 }}>Marktdaten</h1>
        <div className="knopfzeile" style={{ margin: 0 }}>
          <AnleitungKnopf />
          <button className="knopf sekundaer" onClick={auffrischen} disabled={frischt}>
            {frischt ? "Wird geholt …" : "Auffrischen"}
          </button>
        </div>
      </div>

      {fehler && (
        <div className="meldung fehler" role="alert">
          {fehler}
        </div>
      )}

      <div className="kachelfeld">
        {kacheln.map((k) => (
          <KachelKarte
            key={k.code}
            k={k}
            istAdmin={istAdmin}
            beiAenderung={() => api.get<Kachel[]>("/marktdaten").then(setKacheln)}
          />
        ))}
      </div>

      {!kacheln.length && (
        <div className="karte">
          <p className="leer">Keine Kennzahlen aktiv. Unter „Stammdaten“ einschalten.</p>
        </div>
      )}
    </>
  );
}

function KachelKarte({
  k,
  istAdmin,
  beiAenderung,
}: {
  k: Kachel;
  istAdmin: boolean;
  beiAenderung: () => void;
}) {
  const [eingabe, setEingabe] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  const manuell = k.adapter === "manuell";

  async function eintragen(e: React.FormEvent) {
    e.preventDefault();
    const e4 = parseEuroToE4(eingabe);
    if (Number.isNaN(e4)) return setMeldung("Ungültige Zahl.");
    setLaeuft(true);
    setMeldung(null);
    try {
      await api.post(`/marktdaten/${k.code}/wert`, { wert_e4: e4 });
      setEingabe("");
      beiAenderung();
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : "Eintragen fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <section className="kachel">
      <div className="kachel-titel">
        {k.bezeichnung}
        {k.titel_zusatz && ` ${k.titel_zusatz}`}
      </div>
      {/* „August 2026" bei den monatlich erfragten Prämien. Beim Kontrakt
          steht das Jahr schon im Titel und braucht keine zweite Zeile. */}
      {k.zeitraum && !k.titel_zusatz && <div className="kachel-zeitraum">{k.zeitraum}</div>}

      {k.wert_e4 === null ? (
        <div className="kachel-wert leer-wert">—</div>
      ) : (
        <div className="kachel-wert">
          {(k.wert_e4 / 10_000).toLocaleString("de-DE", {
            minimumFractionDigits: k.nachkommastellen,
            maximumFractionDigits: k.nachkommastellen,
          })}
          <span className="kachel-einheit">{k.einheit}</span>
        </div>
      )}

      <div className="kachel-fuss">
        {k.wert_e4 === null && k.zeitraum ? (
          // Nicht „nie abgerufen": Für den Vormonat kann sehr wohl etwas
          // gepflegt sein — es gilt nur nicht mehr.
          `für ${k.zeitraum} noch nicht gepflegt`
        ) : (
          <>
            {k.quelle_stand && <>Stand {zeitpunkt(k.quelle_stand)} · </>}
            {alter(k.abgerufen_am)}
            {k.herkunft === "manuell" && " · manuell"}
          </>
        )}
      </div>

      {/*
        Altershinweis für die Kontraktnotierungen.

        Sie haben bewusst KEINEN Monatsverfall wie die Prämien — ein
        Dezemberkontrakt gilt bis zu seiner Fälligkeit, und eine Monatsgrenze
        würde am Ersten die halbe Kurve ungültig machen. Statt zu blockieren
        steht hier, wie alt der Kurs ist: Bei einem Jahreskontrakt über
        1.200 t bewegen zwei Wochen Kursdrift schnell einen vierstelligen
        Betrag.

        Nur wo es etwas zu sagen gibt: Eine Reihe mit Monatsgrenze fällt von
        selbst aus der Rechnung, sobald der Monat wechselt, und ein
        automatisch abgerufener Wert wird ohnehin nachgeholt. Nicht an
        `zeitraum` festmachen — den tragen Kontrakte auch, dort steht der
        Fälligkeitsmonat.
      */}
      {k.stand_tage !== null && k.stand_tage >= STAND_HINWEIS_TAGE && !k.monatsgrenze && manuell && (
        <div className="kachel-alt">
          Stand ist {k.stand_tage} Tage alt — vor einem Angebot nachziehen.
        </div>
      )}

      {k.fehler && (
        <div className="kachel-fehler" title={k.fehler}>
          Abruf gestört: {k.fehler}
        </div>
      )}

      {/* CC BY 4.0 verlangt Namensnennung samt Hinweis auf Bearbeitung —
          sichtbar an der Kachel, nicht versteckt im Impressum. Die Art der
          Bearbeitung muss dabei stimmen: Beim Monatsmittel wird gemittelt,
          nicht eine Zeitscheibe herausgegriffen. */}
      {k.lizenz_hinweis && (
        <div className="kachel-lizenz">
          {k.lizenz_hinweis} · bearbeitet:{" "}
          {k.code === "strom_mittel"
            ? "zeitgewichtetes Monatsmittel gebildet und gerundet"
            : "Zeitscheibe ausgewählt und gerundet"}
        </div>
      )}

      {manuell && istAdmin && (
        <form className="kachel-eingabe" onSubmit={eintragen}>
          <input
            /*
              "text", nicht "decimal": Der iOS-Ziffernblock hat keine
              Minustaste, und Prämien duerfen negativ sein — MATIF liefert
              Rouen, niedersaechsischer Weizen muss sich den Weg zum Hafen
              erst verdienen.
            */
            inputMode="text"
            placeholder="Wert"
            aria-label={`Wert in ${k.einheit}`}
            value={eingabe}
            onChange={(e) => setEingabe(e.target.value)}
          />
          <button className="knopf" type="submit" disabled={laeuft || !eingabe.trim()}>
            Eintragen
          </button>
        </form>
      )}
      {manuell && !istAdmin && (
        <div className="kachel-fuss">Pflege durch einen Administrator.</div>
      )}
      {meldung && <div className="kachel-fehler">{meldung}</div>}
    </section>
  );
}

/** Wiederverwendbar in der Stammdatenpflege. */
export const formatWert = (e4: number, stellen: number) => formatE4(e4).padStart(stellen);
